/**
 * Brand identity and theming types shared across frontend, backend, and deck-ui.
 * 
 * Ownership split (post-Wave 1):
 * - Frontend app provider writes app-tier brand vars (--brand-accent, --sidebar-*, etc.)
 * - Deck-ui pure helpers write deck-tier brand vars (--slide-*, --ds-accent, etc.)
 * - Both read from these shared input types
 */

import {
  ThemeFontId,
  ThemeHeadingFontId,
  ThemePresetId,
} from "./white-label";

export interface BrandRoleGraph {
  version: string
  hash: string
  seed: {
    accent: string
    card: string
    deep: string
  }
  diagnostics: {
    semanticHueShiftDegrees: number
    semanticDistance: {
      danger: number
      warning: number
      success: number
    }
    adjustedForContrast: boolean
  }
  tokens: {
    brand: {
      accent: string
      accentForeground: string
      surfaceSoft: string
      surfaceStrong: string
      border: string
      textSubtle: string
      card: string
      cardForeground: string
      deep: string
      deepForeground: string
    }
    content: {
      primary: string
      secondary: string
      muted: string
      inverse: string
    }
    semantic: {
      success: string
      successSoft: string
      warning: string
      warningSoft: string
      danger: string
      dangerSoft: string
      info: string
      infoSoft: string
    }
    chart: string[]
  }
}

export type BrandThemeSeedKey =
  | "accent"
  | "card"
  | "deep"
  | "cta"
  | "destructive"
  | "chartAccent";

export const BRAND_THEME_CONFIG_VERSION = 1 as const;

export const BRAND_CHART_PALETTE_IDS = [
  "forest",
  "ocean",
  "slate",
  "earth",
  "mint",
  "autumn",
  "sunset",
  "coral",
  "lavender",
  "twilight",
  "berry",
  "colorful",
] as const;

export type BrandChartPaletteId = (typeof BRAND_CHART_PALETTE_IDS)[number];

export function isBrandChartPaletteId(value: unknown): value is BrandChartPaletteId {
  return (
    typeof value === "string" &&
    (BRAND_CHART_PALETTE_IDS as readonly string[]).includes(value)
  );
}

export interface BrandThemeSeedPair {
  light?: string | null
  dark?: string | null
}

export interface AmbientFieldStops {
  h1: number
  h2: number
  h3: number
  c: number
}

/**
 * Three saved homepage gradients, in slot order. Slot 0 is the page you land
 * on, slot 1 the next tab, slot 2 the last. The settings card numbers them
 * 1, 2, 3 and does not name the tabs, so the same slots can be reused later.
 */
export type AmbientFieldGradientList = [
  AmbientFieldStops,
  AmbientFieldStops,
  AmbientFieldStops,
]

export interface BrandThemeConfig {
  /**
   * Initial schema version. Future versions must preserve forward compatibility:
   * unknown keys are ignored, and missing keys fall back through the derivation
   * engine so older deck snapshots continue to render.
   */
  version: typeof BRAND_THEME_CONFIG_VERSION
  seeds?: Partial<Record<BrandThemeSeedKey, BrandThemeSeedPair>> | null
  chartPaletteId?: BrandChartPaletteId | null
  /**
   * Manual override for the Home Lab ambient mesh gradient.
   *
   * `gradients`, when set, is the three colors the page paints, already in
   * slot order. The page uses them as saved and does not turn them per tab.
   * `light` / `dark` are the older single triple: the page still turns that
   * triple per tab. A light-only patch must not clobber `dark` or `gradients`.
   * Per slot: `null` = explicitly cleared, absent = not set.
   */
  ambientField?: {
    light?: AmbientFieldStops | null
    dark?: AmbientFieldStops | null
    gradients?: AmbientFieldGradientList | null
  } | null
}

export interface BrandSettings {
  brandPrimaryColor: string | null
  brandSecondaryColor: string | null
  brandThemeConfig?: BrandThemeConfig | null
  brandLogoUrl: string | null
  brandLogoUrlOnDark: string | null
  /**
   * Whether each logo needs a `brightness(0) invert(1)` filter when rendered
   * against chrome of the opposite mode.
   *
   * Server-measured from the image bytes and read-only to clients: writes are
   * ignored, because the browser cannot measure this itself (brand images are
   * served with no CORS header, so a canvas probe never loads them).
   *
   * `null` means not measured — treat it as "invert", the safe default for a
   * bare wordmark. Never treat it as `false`.
   */
  brandLogoNeedsInversion?: boolean | null
  brandLogoOnDarkNeedsInversion?: boolean | null
  brandIconLogoUrl: string | null
  brandDomain: string | null
  brandRoleGraph?: BrandRoleGraph | null
  brandRoleGraphVersion?: string | null
  brandRoleGraphHash?: string | null
  brandRevision?: number
  themePresetId?: ThemePresetId | null
  brandFontFamily?: ThemeFontId | null
  brandHeadingFontFamily?: ThemeHeadingFontId | null
  themeOverrides?: Record<string, string> | null
  shareFooterText?: string | null
}
